import * as React from "react";

import {isPickPack, isUyoMPB}   from "@Common/logic/template-options/template-options-pickpack-data-logic";
import {cs}                     from "@Common/react/chain-services";
import {consumeContext}         from "@Common/react/context";
import {GetLatestValue}         from "@Common/react/get-latest-value";
import {isDevQuan}              from "@Common/ui-components/debug/is-dev-quan";
import {PaywallModal}           from "@Common/ui-components/paywall-modal/paywall-modal";
import {chain}                  from "@Common/utils/fs";

import {PromptCartConflict} from "./prompt-cart-conflict/prompt-cart-conflict";
import {PromptEmulation}    from "./prompt-emulation/prompt-emulation";

import {NeedToShowEmulationModal} from "./need-to-show-emulation-modal";
import {XpressoCheckout}          from "./xpresso-checkout/xpresso-checkout";
import {isLegacyItem} from "@Common/logic/item/is-legacy-item";

export const CustomizeActionNew = (
    {
        sku,
        templateOptions, next, status, item,
        prepareSave,
        customizeFlow,
        location,
        isFromApproval,
        build3Cond,
        forceBuild3
    }
) => cs(
    consumeContext("apis"),
    consumeContext("session"),
    consumeContext("cart"),
    consumeContext("routerHistory"),
    ["getLatestCart", ({cart}, next) => GetLatestValue({
        value: cart.value,
        condition: () => !cart.loading,
        next,
    })],
    ["promptEmulation", ({}, next) => PromptEmulation({
        warnings: [
            (item && !isLegacyItem(item)) && !isUyoMPB(templateOptions) && (
                ((templateOptions.proofEngineType !== 0) && !isPickPack(templateOptions)) ? (
                    "Changing the user will create a new order under their name. Customizations will be lost."
                ) : (
                    item?.proofEngineType === 7 && "Updating the user will remove your customizations."
                )
            )
        ].filter(v=>v),
        next,
    })],
    ["showEmulationModal", (_, next) => NeedToShowEmulationModal({templateOptions, next})],
    ["xpressoCheckout", ({}, next) => XpressoCheckout({

        status,
        sku,
        item,

        isCCTemplate: templateOptions.proofEngineType === 0,
        prepareSave,

        customizeFlow,

        location,
        isFromApproval,
        build3Cond,
        forceBuild3,

        next,
    })],
    ["allowUserPayBypass", ({cart}, next) => next(
        (!templateOptions?.approvalRequired || templateOptions?.allowUserPayException) &&
        !cart.value?.items?.some(item => item.approvalInfo?.approvalRequired && !item.approvalInfo?.allowUserPayException)
    )],
    ["promptCartConflict", ({cart, allowUserPayBypass}, next) => PromptCartConflict({
            templateApprovalRequired: !!templateOptions?.approvalRequired,
            cartApprovalRequired: cart.value?.items?.find(item => item.approvalInfo?.approvalRequired),
            allowUserPayBypass,
            next,
    })],
    ["paywallModal", ({}, next) => PaywallModal({next})],
    ({
        xpressoCheckout, session,
        promptEmulation, showEmulationModal, promptCartConflict,
        apis, getLatestCart, paywallModal
    }) => next(async ({approval, forceCustomize, checkForPaywall=undefined}) => cs(
        async (_, next) => updateDefaultGuestAgent({session, apis, next}),
        ["user", async (_, next) => {
            const emu = showEmulationModal() ? (
                await promptEmulation()
            ) : (
                session.value
            );
            return next(emu)
        }],
        ({}, next) => {
            if(checkForPaywall()){
                paywallModal.show()
            }
            else {
                return next()
            }
        },
        // Check legacy items in cart
        async ({routerHistory}, next) => {
            const cart = await getLatestCart();
            if (!isFromApproval && cartHasLegacyOrder(cart)) {
                const useCart = isDevQuan() ? false : await promptCartConflict({legacyXpressoConflict: true});
                if (useCart) {
                    legacyCheckout();
                } else {
                    await apis.next.cart.emptyCart(cart.cartId);
                    await next();
                }
            } else if (!isFromApproval && cartApprovalConflict({cart, templateOptions})) {
                const useCart = isDevQuan() ? false : await promptCartConflict({legacyXpressoConflict: false});
                if(useCart === "bypass") {
                    await next();
                } else if (useCart) {
                    xpressoCheckoutCart({cart, routerHistory});
                } else {
                    await apis.next.cart.emptyCart(cart.cartId);
                    await next();
                }
            }
            else {
                await next();
            }
        },
        ({user}) => xpressoCheckout({user, approval, forceCustomize}),
    )),
);

const cartHasLegacyOrder = (cart) => {
    return !!cart?.items.find((item) => chain(
        Object.keys(item),
        (keys) => keys.length === 1 && keys[0] === "itemId",
    ));
};

const cartApprovalConflict = ({cart, templateOptions}) => {
    if (cart && cart.items.length !== 0)
    {
        if (!!templateOptions?.allowUserPayException) {
            return cart?.items.some(v => !v.approvalInfo?.allowUserPayException || v.approvalInfo?.idOrderApproval !== templateOptions?.idOrderApproval);
        } else if (!!templateOptions?.approvalRequired) {
            return cart?.items.some(v => v.approvalInfo?.idOrderApproval !== templateOptions?.idOrderApproval);
        } else  {
            return cart?.items.some(v => v.approvalInfo?.approvalRequired || v.approvalInfo?.allowUserPayException);
        }
    }
    else {
        return false;
    }
};

const xpressoCheckoutCart = ({cart, routerHistory}) => {
    routerHistory.push(`/checkout/${cart.cartId}`);
};

const legacyCheckout = () => {
    window.location.href = "/next/checkout.php";
};

const updateDefaultGuestAgent = async ({session, apis, next}) => {
    if(session.data.agent.agentLinkId === "defaultGuest") {
        await apis.next.authenticate.createGuestAgent()
        session.data.agent.agentLinkId = '';
        next()
    } else {
        next()
    }
};
